import React, { useState } from "react";
import * as api from "./electricalApi";

/**
 * Reviewer sign-off, and sending an audit back. Modeled on Csa/CsaReview.js —
 * fully generic to the audit workflow, so this is close to a verbatim port.
 *
 * Three things this screen has to say out loud:
 *
 *   · **Review is optional and never a gate.** The report can be issued from
 *     `submitted`; self-release is the expected normal path. So this screen
 *     must not read as "this audit is stuck until someone signs it".
 *
 *   · **A reviewer may not review their own work.** The server refuses anyone
 *     who prepared the audit. The screen is told the answer by the server
 *     (`can_review`) rather than working it out, so the two can never
 *     disagree.
 *
 *   · **Returning needs a reason.** It goes on the transition record. The
 *     button stays disabled until something is written, because the server
 *     refuses an empty one anyway and a refusal after the click is a worse
 *     way to learn that.
 */
export default function ElectricalReview({ auditId, audit, canReview, canReopen, onChanged }) {
  const [mode, setMode] = useState(null); // null | "approve" | "return" | "reopen"
  const [remarks, setRemarks] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const status = audit && audit.status;

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setMode(null);
      setRemarks("");
      setReason("");
      if (onChanged) onChanged();
    } catch (e) {
      // 409 means somebody else moved it while this screen was open. Saying so
      // is more useful than "something went wrong", and re-reading shows what.
      setError(
        e.isConflict
          ? "This audit changed while you were looking at it — reloading to show where it is now."
          : e.message
      );
      if (e.isConflict && onChanged) onChanged();
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- already reviewed ---------------- */

  if (status === "reviewed") {
    return (
      <div className="cap-writeup">
        <div className="rv-done">
          <h2>Reviewed</h2>
          <p>
            This audit has been signed off
            {audit.reviewed_at ? ` on ${fmtDate(audit.reviewed_at)}` : ""}. The
            reviewer's name appears on the report.
          </p>
          {audit.review_remarks ? (
            <blockquote className="rv-remarks">{audit.review_remarks}</blockquote>
          ) : null}
        </div>

        {canReview ? (
          <ReturnBlock
            mode={mode}
            setMode={setMode}
            reason={reason}
            setReason={setReason}
            busy={busy}
            error={error}
            onReturn={() => act(() => api.returnAudit(auditId, reason.trim()))}
            label="Send this back to draft"
          />
        ) : null}
      </div>
    );
  }

  /* ---------------- submitted, and this viewer cannot review ---------------- */

  if (!canReview) {
    return (
      <div className="cap-writeup">
        <div className="rv-wait">
          <h2>Submitted</h2>
          <p>
            The score is fixed and the report can be issued now — review is
            optional and does not hold it up.
          </p>
          <p className="rv-why">
            {audit && audit.assigned_reviewer_id
              ? "A reviewer was nominated when this audit was booked. If they sign it off, their name appears on the report."
              : "No reviewer was nominated for this audit. It can be issued as it stands, or a reviewer can be nominated when booking the next one."}
          </p>
          <p className="rv-why">
            You cannot review this one yourself — a reviewer may never sign off
            an audit they worked on.
          </p>
        </div>

        <ReopenBlock
          mode={mode}
          setMode={setMode}
          busy={busy}
          error={error}
          canReopen={canReopen}
          onReopen={() => act(() => api.reopenAudit(auditId))}
        />
      </div>
    );
  }

  /* ---------------- submitted, and this viewer may review ---------------- */

  return (
    <div className="cap-writeup">
      <div className="wu-head">
        <div>
          <h2>Review this audit</h2>
          <p>
            Signing off puts your name on the report. Returning it sends the
            audit back to draft so the auditor can change it.
          </p>
        </div>
      </div>

      {error ? <p className="wu-error">{error}</p> : null}

      <div className="rv-summary">
        <span className="rv-pct">
          {audit.overall_pct == null ? "—" : Number(audit.overall_pct) + "%"}
        </span>
        <span className="rv-band">{audit.maturity_band || "—"}</span>
        <span className="rv-meta">
          {audit.report_id}
          {audit.submitted_at ? ` · submitted ${fmtDate(audit.submitted_at)}` : ""}
        </span>
      </div>

      <p className="rv-read">
        Before signing off, the <strong>Report</strong> tab shows exactly what
        the client will receive.
      </p>

      {mode === "approve" ? (
        <div className="rv-panel">
          <label htmlFor="rv-remarks">Remarks — optional, kept on the record</label>
          <textarea
            id="rv-remarks"
            rows={3}
            value={remarks}
            placeholder="e.g. Findings verified against the photographs. Isolation register re-checked on site."
            onChange={(e) => setRemarks(e.target.value)}
          />
          <div className="scope-actions">
            <button type="button" className="small-btn" disabled={busy} onClick={() => setMode(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="cap-btn"
              disabled={busy}
              onClick={() => act(() => api.reviewAudit(auditId, { remarks: remarks.trim() || undefined }))}
            >
              {busy ? "Signing off…" : "Sign off"}
            </button>
          </div>
        </div>
      ) : mode === "return" ? (
        <ReturnBlock
          mode={mode}
          setMode={setMode}
          reason={reason}
          setReason={setReason}
          busy={busy}
          error={null}
          onReturn={() => act(() => api.returnAudit(auditId, reason.trim()))}
          label="Return to the auditor"
        />
      ) : (
        <div className="scope-actions">
          <button type="button" className="small-btn warn" onClick={() => setMode("return")}>
            Return to the auditor
          </button>
          <button type="button" className="cap-btn" onClick={() => setMode("approve")}>
            Sign off
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The auditor takes their own audit back.
 *
 * Separate from ReturnBlock: returning is a reviewer sending work back, so
 * the reason is required and at least ten characters. Reopening is addressed
 * to nobody, so it asks for nothing but a confirm step — one click, not a
 * form, because it clears the submission and the frozen score.
 */
function ReopenBlock({ mode, setMode, busy, error, canReopen, onReopen }) {
  if (!canReopen) return null;

  if (mode !== "reopen") {
    return (
      <div className="scope-actions">
        <button type="button" className="small-btn" onClick={() => setMode("reopen")}>
          Reopen for editing
        </button>
      </div>
    );
  }

  return (
    <div className="rv-panel">
      {error ? <p className="wu-error">{error}</p> : null}
      <p className="rv-note">
        This puts the audit back to draft so you can change your answers. The
        score is recalculated when you submit it again.
      </p>
      <p className="rv-note">
        Any report you have already issued keeps its own copy — reopening does
        not change it.
      </p>
      <div className="scope-actions">
        <button type="button" className="small-btn" disabled={busy} onClick={() => setMode(null)}>
          Cancel
        </button>
        <button type="button" className="cap-btn" disabled={busy} onClick={onReopen}>
          {busy ? "Reopening…" : "Reopen for editing"}
        </button>
      </div>
    </div>
  );
}

function ReturnBlock({ mode, setMode, reason, setReason, busy, error, onReturn, label }) {
  if (mode !== "return") {
    return (
      <div className="scope-actions">
        <button type="button" className="small-btn warn" onClick={() => setMode("return")}>
          {label}
        </button>
      </div>
    );
  }

  const ready = reason.trim().length >= 10;

  return (
    <div className="rv-panel">
      {error ? <p className="wu-error">{error}</p> : null}
      <label htmlFor="rv-reason">
        Why are you returning it? <span className="req">— the auditor sees this</span>
      </label>
      <textarea
        id="rv-reason"
        rows={3}
        value={reason}
        placeholder="e.g. A1-014 is marked NO with no note. The signage finding needs where it was seen."
        onChange={(e) => setReason(e.target.value)}
      />
      <p className="rv-note">
        The audit goes back to draft and can be edited again. The score is
        recalculated when it is next submitted.
      </p>
      <div className="scope-actions">
        <button type="button" className="small-btn" disabled={busy} onClick={() => setMode(null)}>
          Cancel
        </button>
        <button type="button" className="cap-btn warn" disabled={!ready || busy} onClick={onReturn}>
          {busy ? "Returning…" : "Return to draft"}
        </button>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split("-");
  if (!y || !m || !day) return s;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(day)} ${months[Number(m) - 1] || m} ${y}`;
}
